/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import analytics.GoalPortfolioOptimizer
import analytics.PortfolioAllocationPlan
import auth.UserIdManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import models.SavingsGoal
import services.CategoryForecastingService
import repository.DatabaseResult
import repository.SavingsGoalsRepository
import repository.createSavingsGoalsRepositoryWithAuth

/**
 * ViewModel for Web Recommendations screen
 * Uses AI-powered portfolio optimization for unified savings allocation
 */
class WebRecommendationsViewModel(
    private val repository: SavingsGoalsRepository = createSavingsGoalsRepositoryWithAuth(
        getAuthToken = { ApiService.getSharedAuthToken() },
        getUserId = { ApiService.Companion.getSharedUserId() }
    ),
    private val optimizer: GoalPortfolioOptimizer = GoalPortfolioOptimizer(),
    private val userIdManager: UserIdManager = UserIdManager(),
    private val scope: CoroutineScope = CoroutineScope(Dispatchers.Default),
    private val apiService: ApiService = ApiService(),
    private val categoryForecastingService: CategoryForecastingService = CategoryForecastingService()
) {
    var goals: List<SavingsGoal> = emptyList()
        private set

    var allocationPlan: PortfolioAllocationPlan? = null
        private set

    var isLoading: Boolean = false
        private set

    var errorMessage: String? = null
        private set

    var onStateChanged: (() -> Unit)? = null

    init {
        loadGoalsAndGeneratePlan()
    }

    fun loadGoalsAndGeneratePlan() {
        isLoading = true
        notifyStateChanged()

        scope.launch {
            val userId = userIdManager.getUserId()
            when (val result = repository.getActiveGoals(userId)) {
                is DatabaseResult.Success -> {
                    goals = result.data
                    errorMessage = null

                    // Generate unified allocation plan using AI optimizer with REAL Plaid data + forecasts
                    if (goals.isNotEmpty()) {
                        val forecast = calculateCompleteForecast(userId)
                        console.log("💰 Using REAL Plaid data with forecasts: income=${forecast.forecastedMonthlyIncome.toInt()}, expenses=${forecast.totalExpenses.toInt()}")
                        allocationPlan = optimizer.generatePortfolioAllocationPlan(
                            goals = goals,
                            categorySpending = forecast.categorySpending,
                            monthlyIncome = forecast.forecastedMonthlyIncome,
                            expenseForecasts = forecast.categoryForecasts
                        )
                    } else {
                        allocationPlan = null
                    }
                }
                is DatabaseResult.Error -> {
                    errorMessage = result.message
                }
            }
            isLoading = false
            notifyStateChanged()
        }
    }

    /**
     * Calculate complete forecast including income and expenses from Plaid transaction data
     * Uses CategoryForecastingService to generate both income and expense forecasts
     * Same approach as Android - NO STUBS!
     */
    private suspend fun calculateCompleteForecast(userId: String): CompleteForecastData {
        return withContext(Dispatchers.Default) {
            try {
                // Fetch transactions from API server
                val response = apiService.getTransactionsFromServer()
                val transactions = response.transactions
                console.log("💰 WebRecommendationsViewModel: Fetched ${transactions.size} transactions from API")

                // Use CategoryForecastingService to generate complete forecast (income + expenses)
                val forecastResult = categoryForecastingService.generateCompleteForecast(
                    transactions = transactions,
                    forecastPeriods = 3,
                    methodId = "sma_3",
                    extractName = { it.name },
                    extractAmount = { it.amount },
                    extractDate = { it.date }
                )

                val totalExpenses = forecastResult.categoryForecasts.values.sumOf { it.average() }

                console.log("💵 Income forecast: current=${forecastResult.currentMonthlyIncome.toInt()}, forecasted=${forecastResult.forecastedMonthlyIncome.toInt()}")
                console.log("💵 Using AI categorization - ${forecastResult.categorySpending.size} categories: ${forecastResult.categorySpending.keys}")
                console.log("📊 Total forecasted expenses: ${totalExpenses.toInt()}")

                CompleteForecastData(
                    forecastedMonthlyIncome = forecastResult.forecastedMonthlyIncome,
                    categorySpending = forecastResult.categorySpending,
                    categoryForecasts = forecastResult.categoryForecasts,
                    totalExpenses = totalExpenses
                )
            } catch (e: Exception) {
                console.error("❌ WebRecommendationsViewModel: Error calculating forecast - ${e.message}")
                CompleteForecastData(0.0, emptyMap(), emptyMap(), 0.0)
            }
        }
    }

    private fun notifyStateChanged() {
        onStateChanged?.invoke()
    }
}

data class CompleteForecastData(
    val forecastedMonthlyIncome: Double,
    val categorySpending: Map<String, Double>,
    val categoryForecasts: Map<String, List<Double>>,
    val totalExpenses: Double
)
